import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Card, Table, Tag, Button, Space, Drawer, message, Typography, Input, Row, Col,
    Statistic, Select, InputNumber, Modal, Tabs, Descriptions, Empty, Tooltip, Popconfirm,
    Alert, Result, Skeleton,
} from 'antd';
import { ReloadOutlined, DollarOutlined, LinkOutlined } from '@ant-design/icons';
import ApiService from '../../APIServices/ApiService';

const { Text, Paragraph } = Typography;

/**
 * The affiliate programme console.
 *
 * Every affiliate who signed up at youguide.com/business/affiliate/register,
 * what they have sold, and what we owe them. The figures come from the SAME
 * commission rows the partner's own dashboard reads, so this screen and theirs
 * cannot disagree about what they earned.
 *
 * MONEY ARRIVES AS INTEGER MINOR UNITS and is only ever divided for display.
 * Rates arrive as basis points (2000 = 20%) for the same reason: a percentage
 * stored as a float rounds differently in two places, and a commission ledger
 * that drifts a cent a line is unauditable.
 */

const money = (minor, currency = 'EUR') =>
    ((minor || 0) / 100).toLocaleString(undefined, { style: 'currency', currency });

const rate = (bp) => `${Number.isInteger(bp / 100) ? bp / 100 : (bp / 100).toFixed(2)}%`;

const CATEGORY_LABEL = { travel: 'Travel guides', language: 'Language guides', esim: 'eSIMs' };

// Which sales channel earned it. Both write to one ledger — before that,
// "what do we owe this affiliate" had two answers on two screens.
const CHANNEL_LABEL = {
    youtra: { label: 'Storefront', color: 'blue' },
    affiliate_guide: { label: 'Own page', color: 'purple' },
};

const STATUS_TAG = {
    active: { color: 'green', label: 'Active' },
    pending: { color: 'gold', label: 'Pending' },
    disabled: { color: 'red', label: 'Disabled' },
};

const COMMISSION_TAG = {
    pending: { color: 'gold', label: 'Settling' },
    approved: { color: 'blue', label: 'Payable' },
    paid: { color: 'green', label: 'Paid' },
    reversed: { color: 'red', label: 'Reversed' },
};

const AffiliateProgramme = () => {
    const [overview, setOverview] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // Distinguished from "no affiliates": an empty table after a failed request
    // reads as "nobody has signed up", which is the one thing it must not say.
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ kind: 'referral', status: undefined, q: '', sort: 'recent' });

    const [detail, setDetail] = useState({ open: false, loading: false, data: null });
    const [rateModal, setRateModal] = useState({ open: false, saving: false, rates: null });
    const [payoutModal, setPayoutModal] = useState({ open: false, saving: false, method: 'manual', reference: '', notes: '' });

    const currency = (overview && overview.currency) || 'EUR';

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [o, list] = await Promise.all([
                ApiService.getAffiliateProgrammeOverview(),
                ApiService.getProgrammeAffiliates({
                    kind: filters.kind || undefined,
                    status: filters.status,
                    q: filters.q || undefined,
                    sort: filters.sort,
                }),
            ]);
            setOverview(o);
            setRows(list.affiliates || []);
        } catch (e) {
            const detail = e?.response?.status === 401 || e?.response?.status === 403
                ? 'Your admin session has expired. Sign in again.'
                : e?.response?.data?.message || e?.message || 'The backend did not respond.';
            setError(detail);
            setOverview(null);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (id) => {
        setDetail({ open: true, loading: true, data: null });
        try {
            const data = await ApiService.getProgrammeAffiliate(id);
            setDetail({ open: true, loading: false, data });
        } catch (e) {
            message.error(e?.response?.data?.message || 'Could not load the affiliate.');
            setDetail({ open: false, loading: false, data: null });
        }
    };

    const setStatus = async (id, status) => {
        try {
            await ApiService.updateProgrammeAffiliate(id, { status });
            message.success(
                status === 'disabled'
                    // Said explicitly because it is the question an admin asks
                    // before clicking: disabling is reversible and takes
                    // nothing away.
                    ? 'Disabled. Sign-in is blocked and their links stop crediting — commission already earned is untouched.'
                    : 'Affiliate enabled.',
            );
            if (detail.data) await openDetail(id);
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || 'Could not change the status.');
        }
    };

    const saveRates = async () => {
        const id = detail.data?.affiliate?.id;
        if (!id) return;
        setRateModal((m) => ({ ...m, saving: true }));
        try {
            await ApiService.updateProgrammeAffiliate(id, { rates: rateModal.rates });
            message.success('Commission rates saved. Past sales keep the rate they were earned at.');
            setRateModal({ open: false, saving: false, rates: null });
            await openDetail(id);
            load();
        } catch (e) {
            // The backend REFUSES an out-of-range rate rather than clamping it,
            // so its message names the allowed range. Show it verbatim.
            message.error(e?.response?.data?.message || 'Could not save the rates.');
            setRateModal((m) => ({ ...m, saving: false }));
        }
    };

    const recordPayout = async () => {
        const id = detail.data?.affiliate?.id;
        if (!id) return;
        setPayoutModal((m) => ({ ...m, saving: true }));
        try {
            const res = await ApiService.recordProgrammePayout(id, {
                method: payoutModal.method,
                reference: payoutModal.reference,
                notes: payoutModal.notes,
            });
            message.success(`Recorded ${money(res.payout.amountMinor, res.payout.currency)} across ${res.payout.lines} entries.`);
            setPayoutModal({ open: false, saving: false, method: 'manual', reference: '', notes: '' });
            await openDetail(id);
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || 'Could not record the payout.');
            setPayoutModal((m) => ({ ...m, saving: false }));
        }
    };

    const backfill = async () => {
        try {
            const res = await ApiService.backfillGuideCommission();
            message.success(
                res.rows > 0
                    ? `Carried ${res.rows} commission row(s) from ${res.purchases} guide sale(s), ${money(res.amountMinor, res.currency)}.`
                    : 'Nothing to carry over — every guide sale is already in the ledger.',
            );
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || 'Backfill failed.');
        }
    };

    const backfillReferred = async () => {
        try {
            const res = await ApiService.backfillReferredOrders();
            message.success(
                res.rows > 0
                    ? `${res.rows} commission row(s) written from ${res.scanned} referred order(s), ${money(res.amountMinor, res.currency)}.`
                    : `${res.scanned} referred order(s) scanned — all already accounted for.`,
            );
            // Named, because "nothing accrued" has several causes and a count
            // cannot tell them apart.
            (res.skipped || []).forEach((s) =>
                message.warning(`${s.transactionId}: ${s.reason}`, 6),
            );
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || 'Backfill failed.');
        }
    };

    const settle = async () => {
        try {
            const res = await ApiService.settleProgrammeCommission();
            message.success(`${res.approved} entr${res.approved === 1 ? 'y is' : 'ies are'} now payable.`);
            load();
        } catch (e) {
            message.error('Settlement failed.');
        }
    };

    const removeAffiliate = async (id) => {
        try {
            await ApiService.deleteProgrammeAffiliate(id);
            message.success('Affiliate deleted.');
            setDetail({ open: false, loading: false, data: null });
            load();
        } catch (e) {
            message.error(e?.response?.data?.message || 'Could not delete the affiliate.');
        }
    };

    const columns = useMemo(() => [
        {
            title: 'Affiliate',
            dataIndex: 'name',
            render: (name, r) => (
                <Space direction="vertical" size={0}>
                    <a onClick={() => openDetail(r.id)}>{name}</a>
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.email || '—'}</Text>
                </Space>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (s) => <Tag color={(STATUS_TAG[s] || {}).color}>{(STATUS_TAG[s] || {}).label || s}</Tag>,
        },
        {
            title: 'Rates',
            dataIndex: 'rates',
            width: 160,
            render: (r) => (
                <Text style={{ fontSize: 12 }}>
                    {rate(r.travel)} / {rate(r.language)} / {rate(r.esim)}
                </Text>
            ),
        },
        {
            title: 'Clicks',
            dataIndex: 'uniqueClicks',
            width: 100,
            align: 'right',
            // Unique, not raw: a reception desk whose guests scan the same card
            // twice has not sent two people.
            render: (u, r) => (
                <Tooltip title={`${r.clicks} total clicks`}><span>{u}</span></Tooltip>
            ),
        },
        { title: 'Sales', dataIndex: 'salesLines', width: 80, align: 'right' },
        {
            title: 'Revenue',
            dataIndex: 'revenueMinor',
            width: 120,
            align: 'right',
            render: (v) => <Tooltip title="What customers paid on referred lines">{money(v, currency)}</Tooltip>,
        },
        {
            title: 'Coupon sales',
            dataIndex: 'couponSales',
            width: 130,
            align: 'right',
            // A low commission against a heavy discount is the system working.
            // Without the amount beside the count it reads as it failing.
            render: (n, r) => (
                n > 0 ? (
                    <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                        <span>{n}</span>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            {money(r.discountMinor, currency)} off
                        </Text>
                    </Space>
                ) : <Text type="secondary">—</Text>
            ),
        },
        {
            title: 'Earned',
            dataIndex: 'lifetimeMinor',
            width: 120,
            align: 'right',
            render: (v) => <Text strong>{money(v, currency)}</Text>,
        },
        {
            title: 'Payable',
            dataIndex: 'payableMinor',
            width: 150,
            align: 'right',
            render: (v, r) => (
                <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                    <span>{money(v, currency)}</span>
                    {r.payoutReady && <Tag color="green" style={{ margin: 0 }}>Ready to pay</Tag>}
                    {/* Over the minimum but missing bank details: the reason a
                        payout cannot be made belongs here, not in an email. */}
                    {!r.payoutReady && r.overMinimum && (
                        <Tooltip title={`Missing: ${(r.payoutReadiness?.missing || []).join(', ')}`}>
                            <Tag color="orange" style={{ margin: 0 }}>Details missing</Tag>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ], [currency]);

    const d = detail.data;

    return (
        <div style={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Typography.Title level={3} style={{ marginBottom: 0 }}>Affiliate programme</Typography.Title>
                    <Text type="secondary">
                        Every affiliate on youguide.com, what they have sold, and what we owe them.
                    </Text>
                </Col>
                <Col>
                    <Space>
                        <Tooltip title="Accrue paid referred orders that have no commission rows — a failed webhook, or an order placed before zero-value sales were recorded. Safe to run repeatedly.">
                            <Button onClick={backfillReferred}>Backfill referred orders</Button>
                        </Tooltip>
                        <Tooltip title="Carry historical affiliate-guide sales into this ledger. Safe to run repeatedly.">
                            <Button onClick={backfill}>Backfill guide sales</Button>
                        </Tooltip>
                        <Tooltip title="Move every sale past its 30-day settlement window to payable.">
                            <Button icon={<DollarOutlined />} onClick={settle}>Settle due commission</Button>
                        </Tooltip>
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
                    </Space>
                </Col>
            </Row>

            {error && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Could not load the affiliate programme"
                    description={error}
                    action={<Button size="small" onClick={load}>Try again</Button>}
                />
            )}

            {/* Skeletons rather than zeros: "EUR 0.00 payable" is a claim, and
                showing it before the figures arrive is a false one. */}
            {loading && !overview && !error && (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    {[0, 1, 2, 3].map((i) => (
                        <Col xs={12} md={6} key={i}>
                            <Card size="small"><Skeleton active paragraph={{ rows: 1 }} title={{ width: '60%' }} /></Card>
                        </Col>
                    ))}
                </Row>
            )}

            {overview && (
                <>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} md={6}>
                            <Card size="small">
                                <Statistic title="Affiliates" value={overview.affiliates.referral} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {overview.affiliates.disabled} disabled · {overview.affiliates.distribution} white-label
                                </Text>
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card size="small">
                                <Statistic title="Referred orders" value={overview.sales.orders} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {overview.traffic.uniqueClicks} unique visitors · {overview.links} links
                                </Text>
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Commission earned"
                                    value={(overview.balances.lifetimeMinor || 0) / 100}
                                    precision={2}
                                    prefix="€"
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {money(overview.balances.pendingMinor, currency)} still settling
                                </Text>
                            </Card>
                        </Col>
                        <Col xs={12} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Payable now"
                                    value={(overview.balances.payableMinor || 0) / 100}
                                    precision={2}
                                    prefix="€"
                                    valueStyle={{ color: overview.awaitingPayout > 0 ? '#cf1322' : undefined }}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {overview.awaitingPayout} over the {money(overview.payoutMinimumMinor, currency)} minimum
                                </Text>
                            </Card>
                        </Col>
                    </Row>

                    {overview.sales.byCategory.length > 0 && (
                        <Card size="small" style={{ marginBottom: 16 }} title="By product">
                            <Row gutter={16}>
                                {overview.sales.byCategory.map((c) => (
                                    <Col xs={24} md={8} key={c.category}>
                                        <Statistic
                                            title={CATEGORY_LABEL[c.category] || c.category}
                                            value={(c.amountMinor || 0) / 100}
                                            precision={2}
                                            prefix="€"
                                        />
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {c.lines} sold · {money(c.revenueMinor, currency)} revenue
                                        </Text>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    )}
                </>
            )}

            <Card
                size="small"
                title={
                    <Space wrap>
                        <Input.Search
                            allowClear
                            placeholder="Name, company or website"
                            style={{ width: 260 }}
                            onSearch={(q) => setFilters((f) => ({ ...f, q }))}
                        />
                        <Select
                            value={filters.kind}
                            style={{ width: 180 }}
                            onChange={(kind) => setFilters((f) => ({ ...f, kind }))}
                            options={[
                                { value: 'referral', label: 'Commission affiliates' },
                                { value: 'distribution', label: 'White-label partners' },
                                { value: '', label: 'All' },
                            ]}
                        />
                        <Select
                            allowClear
                            placeholder="Any status"
                            style={{ width: 150 }}
                            value={filters.status}
                            onChange={(status) => setFilters((f) => ({ ...f, status }))}
                            options={[
                                { value: 'active', label: 'Active' },
                                { value: 'pending', label: 'Pending' },
                                { value: 'disabled', label: 'Disabled' },
                            ]}
                        />
                        <Select
                            value={filters.sort}
                            style={{ width: 170 }}
                            onChange={(sort) => setFilters((f) => ({ ...f, sort }))}
                            options={[
                                { value: 'recent', label: 'Newest first' },
                                { value: 'earned', label: 'Most earned' },
                                { value: 'payable', label: 'Most payable' },
                                { value: 'clicks', label: 'Most clicks' },
                                { value: 'name', label: 'Name' },
                            ]}
                        />
                    </Space>
                }
            >
                <Table
                    rowKey="id"
                    size="small"
                    loading={loading}
                    dataSource={rows}
                    columns={columns}
                    pagination={{ pageSize: 25, showSizeChanger: true }}
                    scroll={{ x: 1100 }}
                    locale={{
                        emptyText: error ? (
                            <Empty description="Could not load affiliates." />
                        ) : filters.q || filters.status ? (
                            <Empty description="No affiliates match these filters." />
                        ) : (
                            <Empty description="No affiliates yet. Signups from youguide.com appear here immediately." />
                        ),
                    }}
                />
            </Card>

            <Drawer
                open={detail.open}
                width={900}
                onClose={() => setDetail({ open: false, loading: false, data: null })}
                title={d ? d.affiliate.name : 'Affiliate'}
                loading={detail.loading}
                extra={d && (
                    <Space>
                        {d.affiliate.status === 'disabled' ? (
                            <Button onClick={() => setStatus(d.affiliate.id, 'active')}>Enable</Button>
                        ) : (
                            <Popconfirm
                                title="Disable this affiliate?"
                                description="They cannot sign in and their links stop crediting. Commission already earned is kept."
                                onConfirm={() => setStatus(d.affiliate.id, 'disabled')}
                            >
                                <Button danger>Disable</Button>
                            </Popconfirm>
                        )}
                        <Button
                            type="primary"
                            disabled={!d.balances.payoutReady}
                            onClick={() => setPayoutModal({ open: true, saving: false, method: 'manual', reference: '', notes: '' })}
                        >
                            Record payout
                        </Button>
                    </Space>
                )}
            >
                {!d && !detail.loading && (
                    <Result
                        status="warning"
                        title="Could not load this affiliate"
                        extra={<Button onClick={() => setDetail({ open: false, loading: false, data: null })}>Close</Button>}
                    />
                )}
                {d && (
                    <Tabs
                        items={[
                            {
                                key: 'overview',
                                label: 'Overview',
                                children: (
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <Row gutter={16}>
                                            <Col span={6}><Statistic title="Earned" value={d.balances.lifetimeMinor / 100} precision={2} prefix="€" /></Col>
                                            <Col span={6}><Statistic title="Settling" value={d.balances.pendingMinor / 100} precision={2} prefix="€" /></Col>
                                            <Col span={6}><Statistic title="Payable" value={d.balances.payableMinor / 100} precision={2} prefix="€" /></Col>
                                            <Col span={6}><Statistic title="Paid out" value={d.balances.paidMinor / 100} precision={2} prefix="€" /></Col>
                                        </Row>

                                        {!d.balances.payoutReady && d.balances.payableMinor > 0 && (
                                            <Paragraph type="warning" style={{ marginBottom: 0 }}>
                                                {d.balances.payableMinor < d.balances.payoutMinimumMinor
                                                    ? `Below the ${money(d.balances.payoutMinimumMinor, currency)} payout minimum.`
                                                    : `Payout details missing: ${(d.affiliate.payoutReadiness?.missing || []).join(', ')}.`}
                                            </Paragraph>
                                        )}

                                        <Descriptions bordered size="small" column={2}>
                                            <Descriptions.Item label="Status">
                                                <Tag color={(STATUS_TAG[d.affiliate.status] || {}).color}>
                                                    {(STATUS_TAG[d.affiliate.status] || {}).label || d.affiliate.status}
                                                </Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Email">{d.affiliate.email || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Contact">{d.affiliate.contactName || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Country">{d.affiliate.country || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Website">{d.affiliate.website || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Audience">{d.affiliate.audience || '—'}</Descriptions.Item>
                                            <Descriptions.Item label="Traffic">
                                                {d.traffic.uniqueClicks} unique ({d.traffic.clicks} clicks)
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Joined">
                                                {new Date(d.affiliate.createdAt).toLocaleDateString()}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Commission rates" span={2}>
                                                <Space>
                                                    <Tag>Travel {rate(d.affiliate.rates.travel)}</Tag>
                                                    <Tag>Language {rate(d.affiliate.rates.language)}</Tag>
                                                    <Tag>eSIM {rate(d.affiliate.rates.esim)}</Tag>
                                                    <Button
                                                        size="small"
                                                        onClick={() => setRateModal({
                                                            open: true,
                                                            saving: false,
                                                            rates: {
                                                                travelBp: d.affiliate.rates.travel,
                                                                languageBp: d.affiliate.rates.language,
                                                                esimBp: d.affiliate.rates.esim,
                                                            },
                                                        })}
                                                    >
                                                        Change
                                                    </Button>
                                                </Space>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Payout account" span={2}>
                                                {d.affiliate.billing?.hasBankDetails
                                                    ? `${d.affiliate.billing.bankAccountHolder} · ${d.affiliate.billing.bankIbanMasked}`
                                                    : 'Not supplied'}
                                            </Descriptions.Item>
                                        </Descriptions>

                                        {d.byCategory.length === 0 && (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="No sales yet — nothing has been bought through their links."
                                            />
                                        )}
                                        {d.byCategory.length > 0 && (
                                            <Table
                                                size="small"
                                                rowKey="category"
                                                pagination={false}
                                                dataSource={d.byCategory}
                                                columns={[
                                                    { title: 'Product', dataIndex: 'category', render: (c) => CATEGORY_LABEL[c] || c },
                                                    { title: 'Sold', dataIndex: 'lines', align: 'right', width: 80 },
                                                    { title: 'Revenue', dataIndex: 'revenueMinor', align: 'right', width: 130, render: (v) => money(v, currency) },
                                                    { title: 'Commission', dataIndex: 'amountMinor', align: 'right', width: 130, render: (v) => money(v, currency) },
                                                ]}
                                            />
                                        )}

                                        {d.launch && (
                                            <Card size="small" title="Launch kit">
                                                <Descriptions size="small" column={2}>
                                                    <Descriptions.Item label="Setup">{d.launch.status}</Descriptions.Item>
                                                    <Descriptions.Item label="Channel">{d.launch.channel || '—'}</Descriptions.Item>
                                                    <Descriptions.Item label="Audience">{d.launch.audience || '—'}</Descriptions.Item>
                                                    <Descriptions.Item label="Destinations">
                                                        {d.launch.destinationScope === 'worldwide'
                                                            ? 'Worldwide'
                                                            : (d.launch.countries || []).join(', ') || '—'}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Placement" span={2}>
                                                        {d.launch.placementStatus === 'none'
                                                            ? 'Not reported'
                                                            : `${d.launch.placementStatus}${d.launch.placementUrl ? ` — ${d.launch.placementUrl}` : ''}`}
                                                    </Descriptions.Item>
                                                </Descriptions>
                                            </Card>
                                        )}
                                    </Space>
                                ),
                            },
                            {
                                key: 'sales',
                                label: `Sales (${d.sales.length})`,
                                children: (
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        dataSource={d.sales}
                                        pagination={{ pageSize: 20 }}
                                        scroll={{ x: 800 }}
                                        columns={[
                                            {
                                                title: 'Date',
                                                dataIndex: 'purchasedAt',
                                                width: 110,
                                                render: (v) => new Date(v).toLocaleDateString(),
                                            },
                                            {
                                                title: 'Product',
                                                dataIndex: 'productName',
                                                render: (n, r) => (
                                                    <Space direction="vertical" size={0}>
                                                        <span>{n || '—'}</span>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            {CATEGORY_LABEL[r.category] || r.category}
                                                        </Text>
                                                    </Space>
                                                ),
                                            },
                                            {
                                                title: 'Source',
                                                dataIndex: 'channel',
                                                width: 130,
                                                render: (c, r) => (
                                                    <Space direction="vertical" size={0}>
                                                        <Tag color={(CHANNEL_LABEL[c] || {}).color}>
                                                            {(CHANNEL_LABEL[c] || {}).label || c}
                                                        </Tag>
                                                        {/* A level above 0 means this paid them for a
                                                            sub-affiliate's sale, not their own. */}
                                                        {r.level > 0 && (
                                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                                sub-affiliate (L{r.level})
                                                            </Text>
                                                        )}
                                                    </Space>
                                                ),
                                            },
                                            {
                                                title: 'Customer',
                                                dataIndex: 'order',
                                                width: 190,
                                                render: (o) => (o ? o.email : '—'),
                                            },
                                            {
                                                title: 'Paid',
                                                dataIndex: 'baseMinor',
                                                width: 150,
                                                align: 'right',
                                                // List and discount sit under the amount paid.
                                                // A commission of zero against a coupon is a
                                                // different thing from zero against a fault,
                                                // and only this tells them apart.
                                                render: (v, r) => (
                                                    <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                                                        <span>{money(v, currency)}</span>
                                                        {r.discountMinor > 0 && (
                                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                                {money(r.listMinor, currency)} · {money(r.discountMinor, currency)} off
                                                                {/* The code, not just the amount. "Why did this line
                                                                    earn 3c against a EUR 9.99 guide" is answered by
                                                                    naming the coupon, and by nothing else here. */}
                                                                {r.order && r.order.coupon ? ` · ${r.order.coupon.code}` : ''}
                                                            </Text>
                                                        )}
                                                    </Space>
                                                ),
                                            },
                                            { title: 'Rate', dataIndex: 'rateBp', width: 70, align: 'right', render: rate },
                                            {
                                                title: 'Commission',
                                                dataIndex: 'amountMinor',
                                                width: 120,
                                                align: 'right',
                                                render: (v) => <Text strong>{money(v, currency)}</Text>,
                                            },
                                            {
                                                title: 'Status',
                                                dataIndex: 'status',
                                                width: 130,
                                                render: (s, r) => (
                                                    <Tooltip
                                                        title={s === 'pending' ? `Payable ${new Date(r.payableAt).toLocaleDateString()}` : undefined}
                                                    >
                                                        <Tag color={(COMMISSION_TAG[s] || {}).color}>
                                                            {(COMMISSION_TAG[s] || {}).label || s}
                                                        </Tag>
                                                    </Tooltip>
                                                ),
                                            },
                                        ]}
                                        locale={{ emptyText: <Empty description="No sales yet." /> }}
                                    />
                                ),
                            },
                            {
                                key: 'links',
                                label: `Links (${d.links.length})`,
                                children: (
                                    <Table
                                        rowKey="id"
                                        size="small"
                                        dataSource={d.links}
                                        pagination={false}
                                        columns={[
                                            {
                                                title: 'Link',
                                                dataIndex: 'slug',
                                                render: (slug, r) => (
                                                    <Space direction="vertical" size={0}>
                                                        <Text copyable={{ text: `https://www.youguide.com/r/${slug}` }}>
                                                            <LinkOutlined /> /r/{slug}
                                                        </Text>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                            {r.name ? `${r.name} → ` : ''}{r.destPath}
                                                        </Text>
                                                    </Space>
                                                ),
                                            },
                                            { title: 'Clicks', dataIndex: 'clicks', width: 90, align: 'right' },
                                            { title: 'Unique', dataIndex: 'uniqueClicks', width: 90, align: 'right' },
                                            {
                                                title: 'Active',
                                                dataIndex: 'isActive',
                                                width: 90,
                                                render: (a) => (a ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
                                            },
                                        ]}
                                        locale={{ emptyText: <Empty description="No tracked links yet." /> }}
                                    />
                                ),
                            },
                            {
                                key: 'payouts',
                                label: `Payouts (${d.payouts.length})`,
                                children: (
                                    <>
                                        <Paragraph type="secondary">
                                            Recording a payout marks the commission it covers as paid. It does not transfer
                                            money — no payment provider is connected — so record it after you have actually
                                            made the transfer.
                                        </Paragraph>
                                        <Table
                                            rowKey="id"
                                            size="small"
                                            dataSource={d.payouts}
                                            pagination={false}
                                            columns={[
                                                { title: 'Date', dataIndex: 'paidAt', render: (v) => new Date(v).toLocaleDateString() },
                                                { title: 'Amount', dataIndex: 'amountMinor', align: 'right', render: (v, r) => money(v, r.currency) },
                                                { title: 'Method', dataIndex: 'method' },
                                                { title: 'Reference', dataIndex: 'reference', render: (v) => v || '—' },
                                                { title: 'Notes', dataIndex: 'notes', render: (v) => v || '—' },
                                            ]}
                                            locale={{ emptyText: <Empty description="Nothing paid out yet." /> }}
                                        />
                                    </>
                                ),
                            },
                            {
                                key: 'danger',
                                label: 'Danger zone',
                                children: (
                                    <Space direction="vertical">
                                        <Paragraph type="secondary">
                                            Deleting is refused once an affiliate has earned anything — that history is what
                                            they were told they had made. Disable the account instead.
                                        </Paragraph>
                                        <Popconfirm
                                            title="Delete this affiliate?"
                                            description="Their login, links and setup are removed permanently."
                                            onConfirm={() => removeAffiliate(d.affiliate.id)}
                                        >
                                            <Button danger>Delete affiliate</Button>
                                        </Popconfirm>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                )}
            </Drawer>

            <Modal
                open={rateModal.open}
                title="Commission rates"
                confirmLoading={rateModal.saving}
                onOk={saveRates}
                onCancel={() => setRateModal({ open: false, saving: false, rates: null })}
            >
                <Paragraph type="secondary">
                    Applies to sales from now on. Past sales keep the rate they were earned at, because that is
                    what the affiliate was told they had made.
                </Paragraph>
                {rateModal.rates && ['travel', 'language', 'esim'].map((cat) => (
                    <Row key={cat} align="middle" style={{ marginBottom: 12 }}>
                        <Col span={10}>{CATEGORY_LABEL[cat]}</Col>
                        <Col span={14}>
                            <InputNumber
                                min={0}
                                max={cat === 'esim' ? 10 : 30}
                                step={0.5}
                                style={{ width: '100%' }}
                                addonAfter="%"
                                value={rateModal.rates[`${cat}Bp`] / 100}
                                onChange={(v) => setRateModal((m) => ({
                                    ...m,
                                    // Stored in basis points. Rounding here is
                                    // what keeps 2.5% from becoming 249bp.
                                    rates: { ...m.rates, [`${cat}Bp`]: Math.round((v || 0) * 100) },
                                }))}
                            />
                        </Col>
                    </Row>
                ))}
            </Modal>

            <Modal
                open={payoutModal.open}
                title="Record a payout"
                confirmLoading={payoutModal.saving}
                onOk={recordPayout}
                okText="Record as paid"
                onCancel={() => setPayoutModal({ open: false, saving: false, method: 'manual', reference: '', notes: '' })}
            >
                <Paragraph>
                    This marks the full payable balance
                    {d ? ` (${money(d.balances.payableMinor, currency)})` : ''} as paid. Record it after the
                    transfer has actually been made.
                </Paragraph>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                        addonBefore="Method"
                        value={payoutModal.method}
                        onChange={(e) => setPayoutModal((m) => ({ ...m, method: e.target.value }))}
                    />
                    <Input
                        addonBefore="Reference"
                        placeholder="Bank transfer reference"
                        value={payoutModal.reference}
                        onChange={(e) => setPayoutModal((m) => ({ ...m, reference: e.target.value }))}
                    />
                    <Input.TextArea
                        rows={2}
                        placeholder="Notes (optional)"
                        value={payoutModal.notes}
                        onChange={(e) => setPayoutModal((m) => ({ ...m, notes: e.target.value }))}
                    />
                </Space>
            </Modal>
        </div>
    );
};

export default AffiliateProgramme;
